import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBanking = () => {
  const [summary, setSummary] = useState(null);
  const [matches, setMatches] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [preparedPayments, setPreparedPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [processingPayments, setProcessingPayments] = useState(false);
  
  // Neue Überweisung Formular
  const [showNewPayment, setShowNewPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({
    name: '',
    iban: '',
    bic: '',
    amount: '',
    reference: ''
  });

  useEffect(() => {
    loadBankingSummary();
  }, []);

  const loadBankingSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/banking/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Banking summary error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/banking/match-registrations`);
      setMatches(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Matches');
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/banking/transactions?days=30`);
      setTransactions(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Transaktionen');
    }
  };

  const loadPreparedPayments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/banking/payments/prepare-interest`);
      setPreparedPayments(response.data);
      setSelectedPayments([]);
    } catch (error) {
      toast.error('Fehler beim Laden der Zinszahlungen');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'matches' && !matches) loadMatches();
    if (tab === 'transactions' && !transactions) loadTransactions();
    if (tab === 'payments' && !preparedPayments) loadPreparedPayments();
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatIBAN = (iban) => {
    if (!iban) return '-';
    const clean = iban.replace(/\s/g, '');
    return clean.replace(/(.{4})/g, '$1 ').trim();
  };

  const togglePaymentSelection = (payoutId) => {
    setSelectedPayments(prev => 
      prev.includes(payoutId) 
        ? prev.filter(id => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const selectAllPayments = () => {
    if (selectedPayments.length === preparedPayments?.payments?.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(preparedPayments?.payments?.map(p => p.payout_id) || []);
    }
  };

  const executeSelectedPayments = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Zahlung aus');
      return;
    }

    if (!window.confirm(`Möchten Sie ${selectedPayments.length} Zahlung(en) wirklich ausführen?`)) {
      return;
    }

    setProcessingPayments(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/banking/payments/execute-interest`, selectedPayments);
      
      if (response.data.demo_mode) {
        toast.success(`Demo-Modus: ${response.data.processed} Zahlungen als bestätigt markiert`);
      } else {
        toast.success(`${response.data.successful} von ${response.data.total} Zahlungen erfolgreich ausgeführt`);
        if (response.data.failed > 0) {
          toast.error(`${response.data.failed} Zahlungen fehlgeschlagen`);
        }
      }
      
      loadPreparedPayments();
      loadBankingSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Ausführung');
    } finally {
      setProcessingPayments(false);
    }
  };

  const handleNewPaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPayment.name || !newPayment.iban || !newPayment.bic || !newPayment.amount || !newPayment.reference) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/admin/banking/payments/create`, {
        ...newPayment,
        amount: parseFloat(newPayment.amount),
        iban: newPayment.iban.replace(/\s/g, '')
      });
      
      toast.success('Überweisung erfolgreich erstellt');
      setShowNewPayment(false);
      setNewPayment({ name: '', iban: '', bic: '', amount: '', reference: '' });
      loadBankingSummary();
      loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Überweisung fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
      </div>
    );
  }

  if (!summary?.configured) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Revolut API nicht konfiguriert</h3>
        <p className="text-yellow-700 mb-4">
          Um Banktransaktionen zu sehen und Überweisungen zu tätigen, müssen Sie die Revolut Business API einrichten.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-banking">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Banking</h2>
          <p className="text-gray-600 mt-1">Revolut Business Kontoübersicht & Überweisungen</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 text-xs rounded-full ${
            summary.demo_mode ? 'bg-blue-100 text-blue-800' :
            summary.environment === 'production' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {summary.demo_mode ? 'Demo' : summary.environment === 'production' ? 'Live' : 'Sandbox'}
          </span>
          <button
            onClick={loadBankingSummary}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Aktualisieren</span>
          </button>
        </div>
      </div>

      {/* Kontostand Karten */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summary.accounts?.map((account) => (
          <div key={account.id} className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] rounded-xl p-6 text-white">
            <p className="text-gray-400 text-sm">{account.name}</p>
            <p className="text-3xl font-bold text-[#C5A059] mt-2">
              {formatCurrency(account.balance, account.currency)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Status: <span className={account.state === 'active' ? 'text-green-400' : 'text-yellow-400'}>{account.state}</span>
            </p>
          </div>
        ))}
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <p className="text-gray-500 text-sm">Eingänge (30 Tage)</p>
          <p className="text-3xl font-bold text-green-600 mt-2">
            +{formatCurrency(summary.incoming_30d?.total || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {summary.incoming_30d?.count || 0} Transaktionen
          </p>
        </div>
      </div>

      {/* Demo-Hinweis */}
      {summary.demo_mode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-blue-800">Demo-Modus</p>
            <p className="text-sm text-blue-600">
              Sie sehen Beispieldaten. Verbinden Sie Ihr Revolut Business Konto, um echte Transaktionen zu sehen und Überweisungen zu tätigen.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Übersicht' },
            { id: 'payments', label: 'Zinszahlungen' },
            { id: 'transfer', label: 'Überweisung' },
            { id: 'matches', label: 'Abgleich' },
            { id: 'transactions', label: 'Transaktionen' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-[#C5A059] text-[#C5A059]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      
      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold">Letzte Transaktionen</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {summary.recent_transactions?.length === 0 ? (
              <p className="p-4 text-gray-500 text-center">Keine Transaktionen</p>
            ) : (
              summary.recent_transactions?.map((tx) => (
                <div key={tx.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.amount > 0 ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <svg className={`w-5 h-5 ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tx.amount > 0 ? "M12 4v16m8-8H4" : "M20 12H4"} />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{tx.counterparty || tx.type}</p>
                      <p className="text-sm text-gray-500">{tx.reference || '-'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount, tx.currency)}
                    </p>
                    <p className="text-xs text-gray-400">{formatDate(tx.date)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ZINSZAHLUNGEN TAB */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          {!preparedPayments ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : preparedPayments.payments?.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <p className="text-gray-500">Keine ausstehenden Zinszahlungen</p>
            </div>
          ) : (
            <>
              {/* Aktionsleiste */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={selectAllPayments}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {selectedPayments.length === preparedPayments.payments.length ? 'Keine auswählen' : 'Alle auswählen'}
                  </button>
                  <span className="text-gray-500">
                    {selectedPayments.length} von {preparedPayments.payments.length} ausgewählt
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Summe ausgewählt:</p>
                    <p className="text-xl font-bold text-[#C5A059]">
                      {formatCurrency(
                        preparedPayments.payments
                          .filter(p => selectedPayments.includes(p.payout_id))
                          .reduce((sum, p) => sum + p.amount, 0)
                      )}
                    </p>
                  </div>
                  <button
                    onClick={executeSelectedPayments}
                    disabled={selectedPayments.length === 0 || processingPayments}
                    className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {processingPayments ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span>Wird ausgeführt...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span>Überweisen</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Zahlungsliste */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input 
                          type="checkbox" 
                          checked={selectedPayments.length === preparedPayments.payments.length}
                          onChange={selectAllPayments}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kunde</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bankverbindung</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zeitraum</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Betrag</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preparedPayments.payments.map((payment) => (
                      <tr key={payment.payout_id} className={`hover:bg-gray-50 ${selectedPayments.includes(payment.payout_id) ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-3">
                          <input 
                            type="checkbox" 
                            checked={selectedPayments.includes(payment.payout_id)}
                            onChange={() => togglePaymentSelection(payment.payout_id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{payment.customer_name}</p>
                          <p className="text-xs text-gray-500">{payment.customer_email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{payment.bank_account_holder}</p>
                          <p className="text-xs font-mono text-gray-500">{formatIBAN(payment.bank_iban)}</p>
                        </td>
                        <td className="px-4 py-3 text-sm">{payment.period}</td>
                        <td className="px-4 py-3 text-right font-semibold text-[#C5A059]">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            payment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            payment.status === 'due' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {payment.status === 'confirmed' ? 'Bestätigt' : payment.status === 'due' ? 'Fällig' : payment.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* NEUE ÜBERWEISUNG TAB */}
      {activeTab === 'transfer' && (
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-6">Neue Überweisung erfassen</h3>
            
            <form onSubmit={handleNewPaymentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Empfänger *</label>
                <input
                  type="text"
                  value={newPayment.name}
                  onChange={(e) => setNewPayment({...newPayment, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                  placeholder="Name des Empfängers"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IBAN *</label>
                  <input
                    type="text"
                    value={newPayment.iban}
                    onChange={(e) => setNewPayment({...newPayment, iban: e.target.value.toUpperCase()})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent font-mono"
                    placeholder="DE89 3704 0044 0532 0130 00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BIC *</label>
                  <input
                    type="text"
                    value={newPayment.bic}
                    onChange={(e) => setNewPayment({...newPayment, bic: e.target.value.toUpperCase()})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent font-mono"
                    placeholder="COBADEFFXXX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Betrag (EUR) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                  placeholder="0,00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verwendungszweck *</label>
                <input
                  type="text"
                  value={newPayment.reference}
                  onChange={(e) => setNewPayment({...newPayment, reference: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A059] focus:border-transparent"
                  placeholder="z.B. Zinszahlung März 2026"
                />
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setNewPayment({ name: '', iban: '', bic: '', amount: '', reference: '' })}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Zurücksetzen
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#C5A059] text-white font-medium rounded-lg hover:bg-[#B08E4A] flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Überweisung ausführen</span>
                </button>
              </div>
            </form>

            {summary.demo_mode && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Demo-Modus:</strong> Im Demo-Modus werden keine echten Überweisungen ausgeführt.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ABGLEICH TAB */}
      {activeTab === 'matches' && (
        <div className="space-y-4">
          {!matches ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : matches.matches?.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <p className="text-gray-500">Keine passenden Einzahlungen gefunden</p>
              <p className="text-sm text-gray-400 mt-2">
                {matches.pending_registrations} ausstehende Registrierungen, {matches.incoming_transfers} Eingänge geprüft
              </p>
            </div>
          ) : (
            matches.matches.map((match, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border-2 border-green-200 overflow-hidden">
                <div className="bg-green-50 px-4 py-2 flex items-center justify-between">
                  <span className="text-green-800 font-medium flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Mögliche Übereinstimmung
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full ${match.confidence === 'high' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'}`}>
                    {match.confidence === 'high' ? 'Hohe Übereinstimmung' : 'Mittlere Übereinstimmung'}
                  </span>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Registrierung</p>
                    <p className="font-semibold">{match.user.name}</p>
                    <p className="text-sm text-gray-600">{match.user.email}</p>
                    <p className="text-lg font-bold text-[#C5A059] mt-2">
                      {formatCurrency(match.user.planned_investment)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Bankeingang</p>
                    <p className="font-semibold">{match.transaction.counterparty_name || 'Unbekannt'}</p>
                    <p className="text-lg font-bold text-green-600 mt-2">
                      +{formatCurrency(match.transaction.amount, match.transaction.currency)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{formatDate(match.transaction.date)}</p>
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <a 
                    href={`/admin/registrierungen`}
                    className="w-full block text-center py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
                  >
                    Zur Aktivierung →
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* TRANSAKTIONEN TAB */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {!transactions ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gegenpartei</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verwendungszweck</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Betrag</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.transactions?.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{formatDate(tx.created_at)}</td>
                      <td className="px-4 py-3 text-sm capitalize">{tx.type}</td>
                      <td className="px-4 py-3 text-sm">{tx.counterparty_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{tx.reference || '-'}</td>
                      <td className={`px-4 py-3 text-sm text-right font-semibold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount, tx.currency)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          tx.state === 'completed' ? 'bg-green-100 text-green-800' :
                          tx.state === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {tx.state}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBanking;
